// Need to use the React-specific entry point to import createApi
import { createApi } from "@reduxjs/toolkit/query/react";
import { baseRestQueryResult as baseQuery, showNotification } from "@/services/api";
import { TransactionResp, TransactionTotalIncomeAndExpense, TransactionTotalMonth } from "@/types/transaction.type";
import { ListResponse } from "@/types/response.type";


interface TotalMonthsGet {
    year: string
}

// Define a service using a base URL and expected endpoints
export const transactionApi = createApi({
    reducerPath: "transactionApi",
    baseQuery,
    endpoints: (builder) => ({
        getTransactions: builder.query<ListResponse<TransactionResp>, {}>({
            query: (data) => ({ url: `transaction`, params: data }),
            keepUnusedDataFor: 0,
            transformResponse: (rawResult: { data: ListResponse<TransactionResp> }, meta) => {
                return rawResult.data
            },
        }),
        getTransactionTotal: builder.query<TransactionTotalIncomeAndExpense, TotalMonthsGet>({
            query: (data) => ({url: `transaction/total`, params: data}),
            keepUnusedDataFor: 0,
            transformResponse: (rawResult: { data: TransactionTotalIncomeAndExpense }, meta) => {
                return rawResult.data || {income: 0, expense: 0} as TransactionTotalIncomeAndExpense
            },
        }),
        getTransactionTotalMonths: builder.query<TransactionTotalMonth[], void>({
            query: () =>  `transaction/total-months`,
            keepUnusedDataFor: 0,
            transformResponse: (rawResult: { data: TransactionTotalMonth[] }) => {
                return rawResult.data || []
            },
        }),
        getTransaction: builder.query<TransactionResp, string>({
            query: (id) => `transaction/${id}`,
            keepUnusedDataFor: 0,
            transformResponse: (rawResult: { data: TransactionResp }, meta) => {
                return rawResult.data
            },
        }),
        queryTransactions: builder.mutation({
            query: (body) => ({
                url: 'transaction/query',
                method: 'POST',
                body,
            }),
            transformResponse: (rawResult: { data: TransactionResp[] }, meta) => {
                return rawResult.data
            },
        }),
        addTransaction: builder.mutation({
            query: (body) => ({
                url: 'transaction',
                method: 'POST',
                body,
            }),
            async onQueryStarted(body, { queryFulfilled }) {
                try {
                  const { data, meta } = await queryFulfilled
                  // `onSuccess` side-effect
                  showNotification("POST", "success")
                } catch (err: any) {
                  
                  // `onError` side-effect
                  showNotification("POST", "error", err?.error?.data?.message || "error")
                }
            }
        }),
        editTransaction: builder.mutation<any, FormData>({
            query: (body) => ({
                url: `transaction/${body.get("id")}`,
                method: 'PUT',
                body,
            }),
            async onQueryStarted(body, { queryFulfilled }) {
                try {
                  const { data, meta } = await queryFulfilled
                  // `onSuccess` side-effect
                  showNotification("PUT", "success")
                } catch (err: any) {
                  
                  // `onError` side-effect
                  showNotification("PUT", "error", err?.error?.data?.message || "error")
                }
            }
        }),
        deleteTransaction: builder.mutation<string, Partial<string>>({
            query: (id) => ({
                url: `transaction/${id}`,
                method: 'DELETE',
            }),
            async onQueryStarted(body, { queryFulfilled }) {
                try {
                  const { data, meta } = await queryFulfilled
                  // `onSuccess` side-effect
                  showNotification("DELETE", "success")
                } catch (err: any) {
                  
                  // `onError` side-effect
                  showNotification("DELETE", "error", err?.error?.data?.message || "error")
                }
            }
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { useGetTransactionsQuery, useGetTransactionTotalQuery, useGetTransactionTotalMonthsQuery, useGetTransactionQuery, useAddTransactionMutation, useEditTransactionMutation, useDeleteTransactionMutation, useQueryTransactionsMutation } = transactionApi;
